import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CredentialRequestListTemplate } from "../templates";
import { useUserProfile, useCredentialPermissions, useCredentialRequestList, useRequestListActions } from "../hooks";
import { getAllDistricts, getRegionsByDistrict } from "@/Services/regions.service";
import type { District } from "@/models/district";
import type { Region } from "@/models/region";
import type { CredentialRequest } from "../types";
import { CREDENTIAL_TYPES } from "@/models/credentialRequest";
import { autoCreateFunctionRequests, syncFunctionRequestMinisters } from "@/Services/credentials.service";

/**
 * Page: Function Credential Request List
 * Shows Function credential requests (authority positions)
 * One request per district/region per year
 */
export function FunctionCredentialRequestListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  // User profile
  const { userProfile } = useUserProfile();
  const { canSyncAuthorities } = useCredentialPermissions();

  // Sync states
  const [isSyncing, setIsSyncing] = useState(false);
  const [hasAutoCreated, setHasAutoCreated] = useState(false);

  // Initialize state from URL params
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page") || "1", 10));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get("pageSize") || "20", 10));

  // Search state
  const [searchValue, setSearchValue] = useState(searchParams.get("search") || "");

  // Sorting state - default to code descending
  const [sorting, setSorting] = useState<SortingState>([{ id: "code", desc: true }]);

  // Filter state from URL
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [districtFilter, setDistrictFilter] = useState(searchParams.get("districtId") || "");
  const [regionFilter, setRegionFilter] = useState(searchParams.get("regionId") || "");
  const [dateFromFilter, setDateFromFilter] = useState(searchParams.get("dateFrom") || "");
  const [dateToFilter, setDateToFilter] = useState(searchParams.get("dateTo") || "");

  // Filter options
  const [districts, setDistricts] = useState<District[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);

  // Load Function requests with filters (always filter by FUNCTION type)
  const { requests, isLoading, totalPages, totalItems, reloadRequests } =
    useCredentialRequestList({
      statusFilter,
      districtFilter,
      regionFilter,
      dateFromFilter,
      dateToFilter,
      searchValue,
      sorting,
      currentPage,
      pageSize,
      credentialType: CREDENTIAL_TYPES.FUNCTION, // ✅ Filter by FUNCTION type
    });

  // Request actions
  const { isSubmitting, handleApprove, handleReject, handleMarkPrinted, handleMarkDelivered } =
    useRequestListActions({
      userId: userProfile?.id,
      onSuccess: reloadRequests,
    });

  // Load districts on mount
  useEffect(() => {
    const loadDistricts = async () => {
      const data = await getAllDistricts();
      setDistricts(data);
    };
    loadDistricts();
  }, []);

  // Load regions when district filter changes
  useEffect(() => {
    const loadRegions = async () => {
      if (districtFilter) {
        const data = await getRegionsByDistrict(districtFilter);
        setRegions(data);
      } else {
        setRegions([]);
        setRegionFilter("");
      }
    };
    loadRegions();
  }, [districtFilter]);

  // Auto-create Function requests on first visit
  useEffect(() => {
    const autoCreate = async () => {
      if (!userProfile?.id || hasAutoCreated) return;
      
      // Mark as started to prevent concurrent runs
      setHasAutoCreated(true);

      const currentYear = new Date().getFullYear();
      try {
        const { created } = await autoCreateFunctionRequests(currentYear, userProfile.id);
        if (created > 0) {
          reloadRequests();
          toast({
            title: "Solicitudes creadas",
            description: `Se crearon ${created} solicitudes de función automáticamente`,
          });
        }
      } catch (error) {
        console.error("Error auto-creating function requests:", error);
        // Reset flag on error so it can retry
        setHasAutoCreated(false);
      }
    };

    autoCreate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.id, hasAutoCreated]);

  // Handle sync authorities
  const handleSyncAuthorities = async () => {
    if (!canSyncAuthorities) return;

    setIsSyncing(true);
    try {
      const currentYear = new Date().getFullYear();
      const { synced } = await syncFunctionRequestMinisters(currentYear);
      
      toast({
        title: "Sincronización completada",
        description: `Se sincronizaron ${synced} solicitudes con las autoridades actuales`,
      });
      
      reloadRequests();
    } catch (error) {
      console.error("Error syncing authorities:", error);
      toast({
        title: "Error",
        description: "No se pudieron sincronizar las autoridades",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  // Sync filters to URL when they change
  const updateURLParams = (updates: Record<string, string>) => {
    const params = new URLSearchParams();
    
    // Preserve existing filters
    const current = {
      page: currentPage.toString(),
      pageSize: pageSize.toString(),
      search: searchValue,
      status: statusFilter,
      districtId: districtFilter,
      regionId: regionFilter,
      dateFrom: dateFromFilter,
      dateTo: dateToFilter,
      ...updates, // Apply updates
    };
    
    // Only add non-empty values to URL
    Object.entries(current).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    
    setSearchParams(params);
  };

  // Wrap filter setters to update URL
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
    updateURLParams({ status: value, page: "1" });
  };

  const handleDistrictFilterChange = (value: string) => {
    setDistrictFilter(value);
    setRegionFilter(""); // Reset region
    setCurrentPage(1);
    updateURLParams({ districtId: value, regionId: "", page: "1" });
  };

  const handleRegionFilterChange = (value: string) => {
    setRegionFilter(value);
    setCurrentPage(1);
    updateURLParams({ regionId: value, page: "1" });
  };

  const handleDateFromChange = (value: string) => {
    setDateFromFilter(value);
    setCurrentPage(1);
    updateURLParams({ dateFrom: value, page: "1" });
  };

  const handleDateToChange = (value: string) => {
    setDateToFilter(value);
    setCurrentPage(1);
    updateURLParams({ dateTo: value, page: "1" });
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
    updateURLParams({ search: value, page: "1" });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateURLParams({ page: page.toString() });
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    updateURLParams({ pageSize: size.toString(), page: "1" });
  };

  // Handle row click to navigate to detail
  const handleRowClick = (request: CredentialRequest) => {
    navigate(`/credential-requests/${request.id}`); // Reuse existing detail page
  };

  return (
    <div className="space-y-4">
      {/* Sync Authorities Button (needs credential_request.edit) */}
      {canSyncAuthorities && (
        <div className="flex justify-end">
          <Button
            onClick={handleSyncAuthorities}
            disabled={isSyncing}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`mr-2 size-4 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Sincronizando...' : 'Sincronizar Autoridades'}
          </Button>
        </div>
      )}

      <CredentialRequestListTemplate
        title="Credenciales de Función"
        subtitle="Solicitudes para autoridades de distritos y regiones"
        requests={requests}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        sorting={sorting}
        onSortingChange={setSorting}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        districtFilter={districtFilter}
        onDistrictFilterChange={handleDistrictFilterChange}
        regionFilter={regionFilter}
        onRegionFilterChange={handleRegionFilterChange}
        dateFromFilter={dateFromFilter}
        onDateFromFilterChange={handleDateFromChange}
        dateToFilter={dateToFilter}
        onDateToFilterChange={handleDateToChange}
        districts={districts}
        regions={regions}
        onRowClick={handleRowClick}
        onApprove={(request) => handleApprove(request.id)}
        onReject={(requestId, notes) => handleReject(requestId, notes)}
        onMarkPrinted={(request) => handleMarkPrinted(request.id)}
        onMarkDelivered={(request) => handleMarkDelivered(request.id)}
        onCreateNew={() => {}} // Function requests are auto-created, no manual creation
        columnOptions={{
          hideDistrict: false,
          hideRegion: false,
        }}
      />
    </div>
  );
}
